import { useCallback, useContext, useState } from "react";

import { useField } from "formik";

import { Button, FileButton, Link } from "components/elements";
import { ValidationBadge } from "components/form";
import { Label } from "components/form/Label/Label";
import { FieldProps } from "src/types/form";
import { getFieldTestId } from "utils/formik";
import { validateImageBlob } from "utils/image";

import { TEXTS } from "./constants";
import styles from "./ImageUploaderField.scss";
import { ImageUploaderContext } from "./ImageUploaderProvider";
import { buildImageConstraints } from "./utils";

type ImageUploaderFieldProps = FieldProps & {
  preprocess?: (blob: Blob) => Promise<Blob>;
};

export function ImageUploaderField(props: ImageUploaderFieldProps) {
  const {
    name,
    accept = ["image/*"],
    disabled,
    label,
    preprocess,
    required = true,
    tooltip,
    dataTestId,
  } = props;

  const { setImage } = useContext(ImageUploaderContext);
  const [, meta, helpers] = useField<string | undefined>({
    name,
    validate: (value: string | undefined) =>
      !value && required ? TEXTS.FIELD_REQUIRED_ERROR : undefined,
  });
  const [imageError, setImageError] = useState<string | undefined>(undefined);
  const testId = dataTestId || getFieldTestId(name);

  const handleChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    async (e) => {
      if (meta.value?.startsWith("blob:")) {
        URL.revokeObjectURL(meta.value);
      }
      helpers.setTouched(true, false);
      helpers.setValue(undefined);
      setImageError(undefined);
      if (!setImage(name, null)) {
        setImageError(TEXTS.UNKNOWN_IMAGE_ERROR);
        return;
      }
      const file = e.target.files?.[0];
      if (!file) {
        return;
      }
      let blob = file.slice(0, file.size, file.type);
      const error = await validateImageBlob(blob, props);
      if (error) {
        setImageError(error);
        return;
      }
      if (preprocess) {
        blob = await preprocess(blob);
      }
      /*
       * Set the value to the blob URL so that we can display the image before
       * we upload it. Plus, this makes the form validator happy.
       */
      const blobUrl = URL.createObjectURL(blob);
      helpers.setValue(blobUrl);
      const fileToUpload = new File([blob], file.name, { type: file.type });
      setImage(name, fileToUpload, (url) => {
        URL.revokeObjectURL(blobUrl);
        helpers.setValue(url);
      });
    },
    [helpers, meta, name, props, preprocess, setImage]
  );

  const handleReset = useCallback(
    (value: string | undefined) => {
      if (meta.value?.startsWith("blob:")) {
        URL.revokeObjectURL(meta.value);
      }
      helpers.setTouched(true, false);
      helpers.setValue(value);
      setImageError(undefined);
      if (!value) {
        if (!setImage(name, null)) {
          setImageError(TEXTS.UNKNOWN_IMAGE_ERROR);
        }
      }
    },
    [helpers, meta, name, setImage]
  );

  return (
    <div className={styles.container} data-test-id={dataTestId}>
      <Label
        text={label}
        required={required}
        tooltip={tooltip}
        dataTestId={`${testId}-label`}
      >
        <p className={styles.constraints}>
          {buildImageConstraints({ ...props, accept })}
        </p>
        {meta.value && (
          <div className={styles.preview}>
            <Link className={styles.image} href={meta.value} target="_blank">
              <img src={meta.value} />
            </Link>
          </div>
        )}
        <div className={styles.buttons}>
          <FileButton
            accept={accept}
            color="navy"
            disabled={disabled}
            text={
              meta.value
                ? TEXTS.REPLACE_BUTTON_LABEL
                : TEXTS.SELECT_BUTTON_LABEL
            }
            dataTestId={`${testId}-file-button`}
            onChange={handleChange}
          />
          {meta.value && (
            <Button
              disabled={disabled}
              dataTestId={`${testId}-clear-button`}
              onClick={() => handleReset(undefined)}
            >
              {TEXTS.CLEAR_BUTTON_LABEL}
            </Button>
          )}
          {meta.value !== meta.initialValue && (
            <Button
              disabled={disabled}
              dataTestId={`${testId}-discard-button`}
              onClick={() => handleReset(meta.initialValue)}
            >
              {TEXTS.DISCARD_BUTTON_LABEL}
            </Button>
          )}
        </div>
        <ValidationBadge
          error={imageError || meta.error}
          touched={meta.touched}
          dataTestId={`${testId}-validation-badge`}
        />
      </Label>
    </div>
  );
}
